/**
 * Bookings of my sites (media owners): confirm, decline, mount with photos
 * (GPS-stamped), go live, complete — each one tap, in the order they happen.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import { compressImage, getPosition } from '../lib/image';
import { formatRange, inr } from '../lib/format';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Meta,
  PageHero,
  SectionTitle,
  Spinner,
  type Tone,
} from '../components/Primitives';
import { Icon } from '../components/Icon';

export interface Booking {
  id: string;
  listingId: string;
  site: string;
  city: string;
  startsOn: string;
  endsOn: string;
  status: string;
  grossInr: number;
  yourShareInr: number;
  artworkUrl: string | null;
  installPhotoUrl: string | null;
  payoutStatus: string;
  holdExpiresAt: string | null;
  confirmedAt: string | null;
  note: string | null;
}

export function BookingsPage() {
  const T = useT();
  const [data, setData] = useState<Booking[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    api.get<{ bookings: Booking[] }>('/v1/partner/bookings').then(
      (r) => setData(r.bookings),
      (e: unknown) => setError(e instanceof ApiError ? e.message : 'Error'),
    );
  useEffect(() => {
    void load();
  }, []);
  if (error) return <ErrorNote message={error} onRetry={() => void load()} />;
  if (!data) return <Spinner />;
  const STATUS: Record<string, [string, Tone]> = {
    held: [T('रोका गया', 'On hold'), 'warn'],
    payment_pending: [T('पेमेंट बाकी', 'Awaiting payment'), 'warn'],
    booked: [T('बुक', 'Booked'), 'good'],
    artwork_pending: [T('डिज़ाइन का इंतज़ार', 'Waiting for artwork'), 'warn'],
    installed: [T('लगा दिया', 'Mounted'), 'good'],
    live: [T('चालू', 'Live'), 'good'],
    completed: [T('पूरा', 'Completed'), 'neutral'],
    cancelled: [T('रद्द', 'Cancelled'), 'bad'],
  };
  const needsYou = data.filter(
    (b) =>
      (['held', 'payment_pending', 'booked'].includes(b.status) && !b.confirmedAt) ||
      ['booked', 'artwork_pending', 'installed'].includes(b.status),
  );
  const rest = data.filter((b) => !needsYou.includes(b));
  const running = data.filter((b) => ['installed', 'live'].includes(b.status)).length;
  const upcoming = data
    .filter((b) => !['completed', 'cancelled'].includes(b.status))
    .reduce((sum, b) => sum + b.yourShareInr, 0);
  const hero = (
    <PageHero
      icon="bookings"
      title={T('बुकिंग', 'Bookings')}
      sub={T(
        'साइट कन्फ़र्म करें, लगाकर फ़ोटो भेजें — बाकी लुलु सँभालता है।',
        'Confirm the site, mount it and send a photo — Lulu does the rest.',
      )}
      stats={[
        { label: T('आपकी बारी', 'Need you'), value: String(needsYou.length) },
        { label: T('चालू', 'Running'), value: String(running) },
        { label: T('आपकी कमाई', 'Your earnings'), value: inr(upcoming) },
      ]}
    />
  );
  if (data.length === 0)
    return (
      <>
        {hero}
        <EmptyState
          icon="bookings"
          title={T('अभी कोई बुकिंग नहीं', 'No bookings yet')}
          body={T(
            'जब कोई आपकी साइट बुक करेगा, यहाँ दिखेगा।',
            'When someone books your site, it shows here.',
          )}
        />
      </>
    );
  const card = (b: Booking) => (
    <BookingCard
      key={b.id}
      b={b}
      status={STATUS[b.status] ?? [b.status, 'neutral']}
      onChange={() => void load()}
    />
  );
  return (
    <div className="space-y-6">
      {hero}
      {needsYou.length > 0 && (
        <section className="space-y-3">
          <SectionTitle count={needsYou.length}>
            {T('आपकी बारी', 'Waiting for you')}
          </SectionTitle>
          {needsYou.map(card)}
        </section>
      )}
      {rest.length > 0 && (
        <section className="space-y-3">
          <SectionTitle count={rest.length}>{T('बाकी बुकिंग', 'Other bookings')}</SectionTitle>
          {rest.map(card)}
        </section>
      )}
    </div>
  );
}

function BookingCard({
  b,
  status,
  onChange,
}: {
  b: Booking;
  status: [string, Tone];
  onChange: () => void;
}) {
  const T = useT();
  const { lang } = useI18n();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/v1/partner/bookings/${b.id}/${path}`, body);
      onChange();
    } catch (err) {
      setMsg(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    } finally {
      setBusy(false);
    }
  };
  const mounted = async (file: File) => {
    setBusy(true);
    setMsg(T('फ़ोटो तैयार हो रही है…', 'Preparing the photo…'));
    try {
      const img = await compressImage(file);
      const pos = await getPosition();
      const up = await api.post<{ url: string }>('/v1/partner/uploads', { data: img.dataUrl });
      await api.post(`/v1/partner/bookings/${b.id}/installed`, {
        photos: [{ url: up.url, ...(pos ? { lat: pos.latitude, lng: pos.longitude } : {}) }],
      });
      setMsg(null);
      onChange();
    } catch (err) {
      setMsg(
        err instanceof ApiError
          ? err.message
          : T('फ़ोटो नहीं गई। दोबारा कोशिश करें।', 'The photo didn’t go. Try again.'),
      );
    } finally {
      setBusy(false);
    }
  };
  const range = formatRange(b.startsOn, b.endsOn, lang);
  const PAY: Record<string, string> = {
    pending: T('काम पूरा होने के बाद', 'After the campaign'),
    due: T('आने वाला है', 'On its way'),
    disputed: T('विवाद में', 'Disputed'),
    paid: T('मिल गया', 'Paid'),
    held: T('रुका हुआ', 'On hold'),
  };
  const ask = ['held', 'payment_pending', 'booked'].includes(b.status) && !b.confirmedAt;
  return (
    <Card className={b.status === 'completed' || b.status === 'cancelled' ? 'opacity-80' : ''}>
      <div className="flex items-start gap-3">
        <span className="icon-tile h-11 w-11">
          <Icon name="listings" className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[17px] font-bold leading-snug">{b.site}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <Badge tone={status[1]} pulse={ask}>
              {status[0]}
            </Badge>
            <Meta icon="listings">{b.city}</Meta>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="rounded-2xl bg-panel-2 px-3 py-2.5">
          <p className="text-[11px] font-semibold text-muted">{T('तारीख', 'Dates')}</p>
          <p className="text-sm font-semibold">{range.text}</p>
          <p className="text-[11px] text-muted">
            {range.days} {T('दिन', 'days')}
          </p>
        </div>
        <div className="rounded-2xl px-3 py-2.5" style={{ background: 'var(--sec-wash)' }}>
          <p className="text-[11px] font-semibold text-muted">
            {T('आपका हिस्सा', 'Your share')}
          </p>
          <p
            className="tnum font-display text-lg font-bold"
            style={{ color: 'var(--sec-ink)' }}
          >
            {inr(b.yourShareInr)}
          </p>
          <p className="text-[11px] text-muted">{PAY[b.payoutStatus] ?? b.payoutStatus}</p>
        </div>
      </div>

      {b.note && <p className="mt-3 text-xs text-muted">{b.note}</p>}
      <div className="mt-3 flex flex-col gap-2">
        {ask && (
          <button className="btn-primary" disabled={busy} onClick={() => void act('confirm')}>
            <Icon name="check" className="h-5 w-5" strokeWidth={2.4} />
            {T('साइट खाली है, कन्फ़र्म', 'Site is free — confirm')}
          </button>
        )}
        {['booked', 'artwork_pending'].includes(b.status) && (
          <label className="btn-primary cursor-pointer">
            <Icon name="camera" className="h-5 w-5" />
            {T('लगा दिया — फ़ोटो भेजें', 'Mounted — send photo')}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              disabled={busy}
              onChange={(e) => e.target.files?.[0] && void mounted(e.target.files[0])}
            />
          </label>
        )}
        {b.status === 'installed' && (
          <button className="btn-primary" disabled={busy} onClick={() => void act('live')}>
            {T('चालू है', 'It’s live')}
          </button>
        )}
        {b.artworkUrl && (
          <a className="btn-soft" href={b.artworkUrl} target="_blank" rel="noreferrer">
            <Icon name="download" className="h-5 w-5" />
            {T('डिज़ाइन डाउनलोड करें', 'Download the artwork')}
          </a>
        )}
        <div className="flex flex-wrap justify-center gap-x-5">
          {['held', 'payment_pending'].includes(b.status) && (
            <button
              className="btn-quiet"
              disabled={busy}
              onClick={() => setDeclining(!declining)}
            >
              {T('मना करें', 'Decline')}
            </button>
          )}
          {['live', 'installed'].includes(b.status) && (
            <button className="btn-quiet" disabled={busy} onClick={() => void act('complete')}>
              {T('पूरा हुआ', 'Mark completed')}
            </button>
          )}
        </div>
      </div>
      {declining && (
        <div className="mt-2 flex gap-2">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={T('क्यों? (ग्राहक को दिखेगा)', 'Why? (the customer sees this)')}
          />
          <button
            className="btn-secondary !w-auto"
            disabled={busy || reason.trim().length < 3}
            onClick={() => void act('decline', { reason })}
          >
            {T('भेजें', 'Send')}
          </button>
        </div>
      )}
      {msg && <p className="mt-2 text-sm text-muted">{msg}</p>}
    </Card>
  );
}
